import { useTranslation } from 'react-i18next';
import FormatTime from 'components/FormatTime';
import { Instrument } from 'services/api/responses/responseTypes';
import { truncateFileName } from 'services/helpers/general/truncatedFileName';
import { type VegaBundle } from 'services/store/ai/types';
import { formulaToRecord } from 'pages/AIMusicGenerator/helpers/formula';
import useSongStructure from '../../useSongStructure';
import {
  StyledFormulaTag,
  StyledFormulaTagsContainer,
  StyledPreviewTag,
} from './styled';

type Props = {
  bundle: VegaBundle;
};

const FormulaTags = ({ bundle }: Props) => {
  const { t } = useTranslation();

  const { genre, formula, isPreview } = bundle;

  const formulaRecord = formulaToRecord(formula);

  const structure = useSongStructure(formulaRecord.structure_id);

  return (
    <StyledFormulaTagsContainer>
      {isPreview && (
        <StyledPreviewTag label={t('aiMusicGenerator:tagLabels:preview')} />
      )}
      {formulaRecord.user_loop && (
        <>
          <StyledFormulaTag
            label={truncateFileName(formulaRecord.user_loop.loopFileName)}
          />
          <StyledFormulaTag
            label={`${formulaRecord.user_loop.entropy * 100} ${t(
              'aiMusicGenerator:userLoopSelector:entropy'
            )}`}
          />
        </>
      )}
      <StyledFormulaTag label={genre} />
      {formulaRecord.genre_blend && (
        <StyledFormulaTag
          label={t('aiMusicGenerator:tagLabels:blend', {
            genreBlend: formulaRecord.genre_blend,
          })}
        />
      )}
      {formulaRecord.energy && (
        <StyledFormulaTag
          label={`${t(formulaRecord.energy.charAt(0).toUpperCase())}${formulaRecord.energy
            .slice(1)
            .toLowerCase()} ${t('aiMusicGenerator:tagLabels:energy')}`}
        />
      )}
      {formulaRecord.duration && (
        <StyledFormulaTag
          label={<FormatTime time={formulaRecord.duration} />}
        />
      )}
      {formulaRecord.bpm && (
        <StyledFormulaTag
          label={`${formulaRecord.bpm} ${t('aiMusicGenerator:tagLabels:bpm')}`}
        />
      )}
      {formulaRecord.key && (
        <StyledFormulaTag
          label={[
            t(`aiMusicGenerator:key:root:${formulaRecord.key.root}`),
            t(`aiMusicGenerator:key:quality:${formulaRecord.key.quality}`),
          ].join(' ')}
        />
      )}
      {formulaRecord.instruments &&
        Object.keys(formulaRecord.instruments).map((instrumentType) => {
          const count =
            formulaRecord.instruments?.[instrumentType as Instrument];

          if (count === undefined || count === 0) {
            return null;
          }

          return (
            <StyledFormulaTag
              key={instrumentType}
              label={`${t(
                `aiMusicGenerator:instruments:${[instrumentType.replace(' ', '_')]}`
              )}${count > 1 ? ` x${count}` : ''}`}
            />
          );
        })}
      {structure && <StyledFormulaTag label={structure?.name} />}
    </StyledFormulaTagsContainer>
  );
};

export default FormulaTags;
